import React, { useState, useMemo } from "react"; // 1. Import useMemo
import { useDispatch, useSelector } from "react-redux";

import { adminSocketService } from "../../services/adminSocketService";
import {
  setActiveConversationUser,
  setMessagesAction,
  setMessagesLoading,
  setMessagesError,
} from "../../store/Actions/AdminChatActions";

const ConversationList = () => {
  const dispatch = useDispatch();
  const {
    conversations,
    loadingConversations,
    errorConversations,
    activeConversationUserId,
  } = useSelector((state) => state.AdminChatReducer);
  const [activeTab, setActiveTab] = useState("All");

  const handleConversationSelect = (userId) => {
    if (activeConversationUserId === userId) return;
    dispatch(setActiveConversationUser(userId));
    dispatch(setMessagesLoading(true));
    adminSocketService.getHistoryForUser({ user_id: userId }, (response) => {
      if (response.status === "ok") {
        dispatch(setMessagesAction(response.messages));
      } else {
        dispatch(setMessagesError(response.msg));
      }
    });
    adminSocketService.markMessagesAsRead({ user_id: userId });
  };

  const filteredConversations = useMemo(() => {
    if (activeTab === "All") {
      return conversations;
    }
    if (activeTab === "Unread") {
      return conversations.filter((convo) => convo && convo.unread_count > 0);
    }
    if (activeTab === "Read") {
      return conversations.filter(
        (convo) => convo && (!convo.unread_count || convo.unread_count === 0)
      );
    }
    if (activeTab === "Replied") {
      return conversations.filter((convo) => convo && convo.is_admin === 1);
    }
    if (activeTab === "Unreplied") {
      return conversations.filter((convo) => convo && convo.is_admin === 0);
    }
    return conversations;
  }, [conversations, activeTab]); // 3. Dependency array

  // --- JSX Rendering (Unchanged) ---
  if (loadingConversations) {
    return (
      <p className="p-4 text-center text-gray-500">Loading conversations...</p>
    );
  }

  if (errorConversations) {
    return (
      <p className="p-4 text-center text-red-500">
        Error: {errorConversations}
      </p>
    );
  }

  return (
    <div className="bg-white border-r border-gray-200 h-full flex flex-col">
      <div className="sticky top-0 bg-white z-10">
        <h2 className="text-lg font-semibold p-4 border-b border-gray-200">
          Conversations
        </h2>

        <div className="flex gap-2 p-2 border-b bg-gray-50 overflow-x-auto">
          <button
            className={`px-3 py-1 rounded font-medium text-sm whitespace-nowrap ${
              activeTab === "All"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
            onClick={() => setActiveTab("All")}
          >
            All
          </button>
          <button
            className={`px-3 py-1 rounded font-medium text-sm whitespace-nowrap ${
              activeTab === "Unread"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
            onClick={() => setActiveTab("Unread")}
          >
            Unread
          </button>
          <button
            className={`px-3 py-1 rounded font-medium text-sm whitespace-nowrap ${
              activeTab === "Read"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
            onClick={() => setActiveTab("Read")}
          >
            Read
          </button>
          <button
            className={`px-3 py-1 rounded font-medium text-sm whitespace-nowrap ${
              activeTab === "Replied"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
            onClick={() => setActiveTab("Replied")}
          >
            Replied
          </button>
          <button
            className={`px-3 py-1 rounded font-medium text-sm whitespace-nowrap ${
              activeTab === "Unreplied"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
            onClick={() => setActiveTab("Unreplied")}
          >
            Unreplied
          </button>
        </div>
      </div>

      {filteredConversations.length === 0 ? (
        <div className="flex-grow flex items-center justify-center">
          <p className="p-4 text-center text-gray-500">
            No conversations in this category.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 overflow-y-auto flex-grow">
          {filteredConversations.map((convo) => (
            <li
              key={convo.user_id}
              onClick={() => handleConversationSelect(convo.user_id)}
              className={`p-3 hover:bg-gray-100 cursor-pointer transition-colors duration-150 ${
                activeConversationUserId === convo.user_id
                  ? "bg-blue-100 border-l-4 border-blue-500"
                  : "border-l-4 border-transparent"
              }`}
            >
              <div className="flex justify-between items-center">
                <p className="font-medium text-sm truncate">
                  {convo.first_name
                    ? `${convo.first_name} ${convo.last_name || ""}`
                    : `User ID: ${convo.user_id}`}
                </p>
                {convo.unread_count > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                    {convo.unread_count}
                  </span>
                )}
              </div>
              <p
                className={`text-xs truncate mt-1 ${
                  convo.is_admin === 0 && convo.unread_count > 0
                    ? "text-red-500 font-bold"
                    : "text-gray-600"
                }`}
              >
                {convo.is_admin === 1 && "You: "}
                {convo.message}
              </p>
              <p className="text-xs text-gray-400 mt-1 text-right">
                {new Date(convo.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}{" "}
                |{" "}
                {new Date(convo.created_at).toLocaleDateString([], {
                  day: "2-digit",
                  month: "short",
                })}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ConversationList;
